import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import { SignaturePad } from "@/components/SignaturePad";
import { money, computeTotalsPerLine, DOC_META, VAT_RATES, QTY_UNITS, isHazardousEwc } from "@/lib/format";
import { Plus, Trash2, Check, ArrowRight, ArrowLeft, TriangleAlert } from "lucide-react";

const LATE_PAYMENT = "Payment due within 14 days of the invoice date. We reserve the right to charge interest and reasonable compensation on overdue amounts under the Late Payment of Commercial Debts (Interest) Act 1998.";
const today = () => new Date().toISOString().slice(0, 10);

const Label = ({ children }) => <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{children}</label>;
const Box = ({ testid, ...p }) => <input data-testid={testid} {...p} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-3.5 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary transition-colors placeholder:text-muted-foreground" />;
const Area = ({ testid, ...p }) => <textarea data-testid={testid} {...p} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-3.5 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary transition-colors placeholder:text-muted-foreground" />;

const dec = (v) => {
  v = String(v ?? "").replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  return parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : v;
};

export default function DocumentEditor() {
  const { type: routeType, id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editing = !!id;
  const vatOn = !!user?.vat_registered;
  const defaultVat = vatOn ? 20 : 0;

  const [type, setType] = useState(routeType || "quote");
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(sp.get("client") || "");
  const [nc, setNc] = useState({ name: "", phone: "", email: "", address: "" });
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [docDate, setDocDate] = useState(today());
  const [supplyDate, setSupplyDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [deposit, setDeposit] = useState("");
  const [items, setItems] = useState([{ description: "", qty: "1", unit_price: "", vat_rate: defaultVat }]);
  const [validUntil, setValidUntil] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState(LATE_PAYMENT);
  const [paymentDetails, setPaymentDetails] = useState("");
  const [waste, setWaste] = useState({
    description: "", ewc_code: "", quantity: "", quantity_unit: "tonnes",
    container: "loose", container_type: "", container_size: "",
    date_of_transfer: today(), time_of_transfer: "", place_of_transfer: "",
    sic_code: "", permit_details: "", broker: "", destination_name: "", destination_permit: "",
    signatures: { transferor: "", transferee: "" },
  });

  const meta = DOC_META[type] || {};
  const isLineDoc = type === "quote" || type === "invoice";
  const hazardous = type === "waste_note" && isHazardousEwc(waste.ewc_code);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data.clients)); }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/documents/${id}`).then(({ data }) => {
      const d = data.document; const dt = d.data || {};
      setType(d.type); setClientId(d.client_id || "");
      if (!d.client_id && d.client) setNc({ name: d.client.name || "", phone: d.client.phone || "", email: d.client.email || "", address: d.client.address || "" });
      setTitle(dt.title || ""); setSiteAddress(dt.site_address || ""); setNotes(dt.notes || ""); setTerms(dt.terms || "");
      setDeposit(dt.deposit || ""); setDocDate(dt.doc_date || today()); setSupplyDate(dt.supply_date || today());
      setValidUntil(dt.valid_until || ""); setDueDate(dt.due_date || "");
      setPaymentTerms(dt.payment_terms || LATE_PAYMENT); setPaymentDetails(dt.payment_details || "");
      if (dt.line_items?.length) setItems(dt.line_items.map((i) => ({ description: i.description, qty: String(i.qty ?? 1), unit_price: String(i.unit_price ?? ""), vat_rate: i.vat_rate == null ? (dt.tax_rate || 0) : i.vat_rate })));
      if (d.type === "waste_note") setWaste((w) => ({ ...w, ...dt, signatures: { transferor: dt.signatures?.transferor || "", transferee: dt.signatures?.transferee || "" } }));
    });
    // eslint-disable-next-line
  }, [id]);

  const totals = useMemo(() => computeTotalsPerLine(items.map((i) => ({ qty: Number(i.qty), unit_price: Number(i.unit_price), vat_rate: Number(i.vat_rate) }))), [items]);

  const setItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems([...items, { description: "", qty: "1", unit_price: "", vat_rate: defaultVat }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const quickValid = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setValidUntil(d.toISOString().slice(0, 10)); };
  const setW = (k, v) => setWaste((w) => ({ ...w, [k]: v }));

  const buildData = () => {
    if (type === "waste_note") return { title, site_address: siteAddress, doc_date: docDate, ...waste, notes };
    const lineItems = items.filter((i) => i.description.trim() || Number(i.unit_price) > 0)
      .map((i) => ({ description: i.description.trim(), qty: Number(i.qty || 0), unit_price: Number(i.unit_price || 0), vat_rate: Number(i.vat_rate || 0) }));
    const t = computeTotalsPerLine(lineItems);
    const base = { title, site_address: siteAddress, doc_date: docDate, line_items: lineItems, notes, terms, ...t };
    if (type === "quote") { base.valid_until = validUntil; base.deposit = deposit; }
    if (type === "invoice") { base.supply_date = supplyDate; base.due_date = dueDate; base.payment_terms = paymentTerms; base.payment_details = paymentDetails; }
    return base;
  };

  const step1Valid = () => {
    if (!clientId && !nc.name.trim()) return false;
    if (type === "waste_note") return !!waste.description.trim() && !!waste.ewc_code.trim() && !!waste.quantity.trim() && !hazardous;
    return true;
  };

  const save = async () => {
    if (type === "waste_note" && hazardous) { toast.error("Hazardous EWC code — a consignment note is required, not a transfer note."); return; }
    if (!clientId && !nc.name.trim()) { toast.error("Add a client"); setStep(1); return; }
    setSaving(true);
    const payload = { type, data: buildData(), client_id: clientId || null };
    if (!clientId && nc.name.trim()) payload.client = { name: nc.name.trim(), phone: nc.phone.trim(), email: nc.email.trim(), address: nc.address.trim() };
    try {
      if (editing) { await api.patch(`/documents/${id}`, { data: payload.data, client_id: payload.client_id, client: payload.client }); toast.success("Saved"); navigate(`/doc/${id}`, { replace: true }); }
      else { const { data } = await api.post("/documents", payload); toast.success(`${meta.label} created`); navigate(`/doc/${data.document.id}`, { replace: true }); }
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Trial ended — subscribe to continue"); navigate("/paywall"); }
      else toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const goStep2 = () => { if (!step1Valid()) { toast.error("Fill in the required fields first"); return; } setStep(2); };

  return (
    <Shell back title={editing ? `Edit ${meta.label}` : `New ${meta.label}`} showNav={false}>
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-5">
        {[1, 2].map((s) => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${step >= s ? "bg-brandPrimary text-onBrand" : "bg-surfaceTertiary text-muted-foreground"}`}>{s}</div>
            <span className={`text-xs font-bold ${step >= s ? "text-onSurface" : "text-muted-foreground"}`}>{s === 1 ? "Details" : type === "waste_note" ? "Declaration & sign" : "Items & totals"}</span>
            {s === 1 && <div className={`flex-1 h-0.5 rounded ${step > 1 ? "bg-brandPrimary" : "bg-surfaceTertiary"}`} />}
          </div>
        ))}
      </div>

      <div className="space-y-5 pb-28 animate-fade-up">
        {step === 1 && (
          <>
            {/* Client */}
            <div>
              <Label>Client</Label>
              <select data-testid="doc-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-3.5 py-3 text-base focus:outline-none focus:border-brandPrimary">
                <option value="">— New / one-off client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!clientId && (
                <div className="mt-2 space-y-2">
                  <Box testid="doc-new-client-name" placeholder="Client name *" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} />
                  <Box testid="doc-new-client-email" type="email" placeholder="Client email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} />
                  <Box testid="doc-new-client-phone" type="tel" inputMode="tel" placeholder="Client mobile" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
                  <Box testid="doc-new-client-address" placeholder="Client address" value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} />
                </div>
              )}
            </div>

            <div><Label>Title</Label><Box testid="doc-title" placeholder="Skip hire, 8 yard, 14 days" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <div><Label>Job / site address</Label><Box testid="doc-site-address" placeholder="Where the work / collection takes place" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} /></div>

            {type === "quote" && (
              <>
                <div><Label>Quote date</Label><Box testid="doc-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></div>
                <div>
                  <Label>Valid until</Label>
                  <div className="flex gap-2 mb-2">{[7, 14, 30, 60].map((d) => <button key={d} data-testid={`valid-${d}`} onClick={() => quickValid(d)} className="flex-1 bg-surfaceTertiary rounded-full py-2 text-xs font-semibold hover:bg-brandTertiary transition-colors">{d} days</button>)}</div>
                  <Box testid="doc-valid-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
              </>
            )}

            {type === "invoice" && (
              <div className="flex gap-3">
                <div className="flex-1"><Label>Invoice date</Label><Box testid="doc-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></div>
                <div className="flex-1"><Label>Date of supply</Label><Box testid="doc-supply-date" type="date" value={supplyDate} onChange={(e) => setSupplyDate(e.target.value)} /></div>
              </div>
            )}

            {type === "waste_note" && (
              <div className="space-y-4">
                <div><Label>Description of waste *</Label><Box testid="waste-description" value={waste.description} onChange={(e) => setW("description", e.target.value)} placeholder="e.g. Mixed household clearance" /></div>
                <div>
                  <Label>EWC / List-of-Waste code *</Label>
                  <Box testid="waste-ewc" value={waste.ewc_code} onChange={(e) => setW("ewc_code", e.target.value)} placeholder="e.g. 20 03 01" />
                  {hazardous && (
                    <div data-testid="hazard-warning" className="mt-2 flex items-start gap-2 bg-error/10 border border-error/30 rounded-[12px] p-3">
                      <TriangleAlert size={16} className="text-error shrink-0 mt-0.5" />
                      <p className="text-xs text-error font-semibold">This code is hazardous (marked *). Hazardous waste needs a <span className="underline">consignment note</span>, not a transfer note — you can't save this as a transfer note.</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Quantity *</Label><Box testid="waste-qty" inputMode="decimal" value={waste.quantity} onChange={(e) => setW("quantity", e.target.value)} placeholder="e.g. 2" /></div>
                  <div className="w-36"><Label>Units</Label>
                    <select data-testid="waste-qty-unit" value={waste.quantity_unit} onChange={(e) => setW("quantity_unit", e.target.value)} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-3 py-3 text-base focus:outline-none focus:border-brandPrimary">
                      {QTY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Loose or in a container?</Label>
                  <div className="flex gap-2">
                    {["loose", "container"].map((v) => <button key={v} data-testid={`waste-cont-${v}`} onClick={() => setW("container", v)} className={`flex-1 rounded-full py-2.5 text-sm font-semibold capitalize transition-colors ${waste.container === v ? "bg-brandPrimary text-onBrand" : "bg-surfaceTertiary text-onSurface"}`}>{v}</button>)}
                  </div>
                  {waste.container === "container" && (
                    <div className="mt-2 flex gap-3">
                      <div className="flex-1"><Box testid="waste-cont-type" value={waste.container_type} onChange={(e) => setW("container_type", e.target.value)} placeholder="Type (e.g. skip)" /></div>
                      <div className="flex-1"><Box testid="waste-cont-size" value={waste.container_size} onChange={(e) => setW("container_size", e.target.value)} placeholder="Size (e.g. 8 yard)" /></div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Date of transfer</Label><Box testid="waste-date" type="date" value={waste.date_of_transfer} onChange={(e) => setW("date_of_transfer", e.target.value)} /></div>
                  <div className="flex-1"><Label>Time of transfer</Label><Box testid="waste-time" type="time" value={waste.time_of_transfer} onChange={(e) => setW("time_of_transfer", e.target.value)} /></div>
                </div>
                <div><Label>Place of transfer</Label><Box testid="waste-place" value={waste.place_of_transfer} onChange={(e) => setW("place_of_transfer", e.target.value)} placeholder="Where the waste changed hands" /></div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Client SIC code (optional)</Label><Box testid="waste-sic" value={waste.sic_code} onChange={(e) => setW("sic_code", e.target.value)} placeholder="e.g. 38110" /></div>
                  <div className="flex-1"><Label>Permit / exemption (optional)</Label><Box testid="waste-permit" value={waste.permit_details} onChange={(e) => setW("permit_details", e.target.value)} placeholder="e.g. NWFD 123456" /></div>
                </div>
                <div><Label>Broker / dealer (optional)</Label><Box testid="waste-broker" value={waste.broker} onChange={(e) => setW("broker", e.target.value)} placeholder="If a broker arranged it" /></div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Destination (optional)</Label><Box testid="waste-dest-name" value={waste.destination_name} onChange={(e) => setW("destination_name", e.target.value)} placeholder="Tip / transfer station" /></div>
                  <div className="flex-1"><Label>Dest. permit no. (optional)</Label><Box testid="waste-dest-permit" value={waste.destination_permit} onChange={(e) => setW("destination_permit", e.target.value)} placeholder="Permit number" /></div>
                </div>
              </div>
            )}

            <div><Label>Notes</Label><Area testid="doc-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the client should know…" /></div>
          </>
        )}

        {step === 2 && isLineDoc && (
          <>
            <div>
              <Label>Line items{vatOn ? " (VAT per line)" : ""}</Label>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="bg-surface border border-border rounded-[12px] p-3">
                    <input data-testid={`item-desc-${i}`} placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="w-full bg-transparent text-base font-semibold text-onSurface focus:outline-none mb-2" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1 flex-1 min-w-[70px]">
                        <span className="text-xs text-muted-foreground">Qty</span>
                        <input data-testid={`item-qty-${i}`} type="text" inputMode="decimal" value={it.qty} onChange={(e) => setItem(i, "qty", dec(e.target.value))} className="w-full bg-surfaceSecondary rounded-[8px] px-2 py-1.5 text-base focus:outline-none" />
                      </div>
                      <div className="flex items-center gap-1 flex-1 min-w-[80px]">
                        <span className="text-xs text-muted-foreground">£</span>
                        <input data-testid={`item-price-${i}`} type="text" inputMode="decimal" placeholder="0.00" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", dec(e.target.value))} className="w-full bg-surfaceSecondary rounded-[8px] px-2 py-1.5 text-base focus:outline-none" />
                      </div>
                      {vatOn && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">VAT</span>
                          <select data-testid={`item-vat-${i}`} value={it.vat_rate} onChange={(e) => setItem(i, "vat_rate", Number(e.target.value))} className="bg-surfaceSecondary rounded-[8px] px-2 py-1.5 text-sm focus:outline-none">
                            {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                      )}
                      {items.length > 1 && <button data-testid={`item-remove-${i}`} onClick={() => rmItem(i)} className="text-muted-foreground p-1"><Trash2 size={16} /></button>}
                    </div>
                    <div className="text-right text-sm font-bold text-onSurface mt-1">{money(Number(it.qty || 0) * Number(it.unit_price || 0))}</div>
                  </div>
                ))}
              </div>
              <button data-testid="add-item-btn" onClick={addItem} className="mt-2 flex items-center gap-1.5 text-brandPrimary font-semibold text-sm"><Plus size={16} /> Add item</button>

              <div className="mt-4 bg-surface border border-border rounded-[12px] p-3.5 space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{money(totals.subtotal)}</span></div>
                {vatOn && <div className="flex justify-between text-sm"><span className="text-muted-foreground">VAT</span><span className="font-semibold">{money(totals.tax)}</span></div>}
                <div className="flex justify-between text-base font-heading font-extrabold pt-1 border-t border-border"><span>Total</span><span className="text-brandPrimary">{money(totals.total)}</span></div>
              </div>
              {!vatOn && <p className="text-xs text-muted-foreground mt-2">Not VAT registered — turn it on in Account → Business details to add VAT per line.</p>}
            </div>

            {type === "quote" && (
              <>
                <div><Label>Deposit / payment terms</Label><Box testid="doc-deposit" placeholder="e.g. 50% deposit to book, balance on completion" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
                <div><Label>Terms &amp; conditions</Label><Area testid="doc-terms" rows={4} placeholder="e.g. No plasterboard, tyres or fridges. Overloaded skips cannot be collected. Extra days charged at £X/day." value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
              </>
            )}

            {type === "invoice" && (
              <>
                <div><Label>Due date</Label><Box testid="doc-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
                <div><Label>Payment terms</Label><Area testid="doc-payment-terms" rows={3} value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} /></div>
                <div><Label>Payment details</Label><Box testid="doc-payment-details" placeholder="Sort code · Account no · Bank" value={paymentDetails} onChange={(e) => setPaymentDetails(e.target.value)} /></div>
              </>
            )}
          </>
        )}

        {step === 2 && type === "waste_note" && (
          <div className="space-y-5">
            <div className="bg-surfaceTertiary rounded-[12px] p-4">
              <p className="text-xs font-bold text-muted-foreground mb-1">Waste hierarchy declaration (printed on the note)</p>
              <p className="text-sm text-onSurface leading-relaxed">"I confirm I have fulfilled my duty to apply the waste hierarchy as required by Regulation 12 of the Waste (England and Wales) Regulations 2011."</p>
            </div>
            <div>
              <Label>Transferor signature (you — handing over)</Label>
              <SignaturePad testid="sign-transferor" value={waste.signatures.transferor} onChange={(v) => setWaste((w) => ({ ...w, signatures: { ...w.signatures, transferor: v } }))} />
            </div>
            <div>
              <Label>Transferee signature (receiver)</Label>
              <SignaturePad testid="sign-transferee" value={waste.signatures.transferee} onChange={(v) => setWaste((w) => ({ ...w, signatures: { ...w.signatures, transferee: v } }))} />
            </div>
            <p className="text-xs text-muted-foreground">Your carrier licence &amp; details from your profile are added to the note automatically. This note is kept for 2+ years and can't be deleted.</p>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-surface/95 backdrop-blur-md border-t border-border p-4 flex gap-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
        {step === 2 && (
          <button data-testid="doc-back-btn" onClick={() => setStep(1)} className="w-14 shrink-0 bg-surfaceTertiary text-onSurface rounded-full h-13 py-3.5 flex items-center justify-center active:scale-95 transition-transform"><ArrowLeft size={20} /></button>
        )}
        {step === 1 ? (
          <button data-testid="doc-next-btn" onClick={goStep2} disabled={hazardous} className="flex-1 bg-brandPrimary text-onBrand rounded-full h-13 py-3.5 font-heading font-bold flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-50">
            Continue <ArrowRight size={20} />
          </button>
        ) : (
          <button data-testid="doc-save-btn" disabled={saving || hazardous} onClick={save} className="flex-1 bg-brandPrimary text-onBrand rounded-full h-13 py-3.5 font-heading font-bold flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-50">
            <Check size={20} /> {saving ? "Saving…" : editing ? "Save changes" : `Create ${meta.label}`}
          </button>
        )}
      </div>
    </Shell>
  );
}
